import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AbandonWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Commands/AbandonWorkflowRun';
import abandonWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/abandonWorkflowRun';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '@verclam/synapse';
import { ConfirmDialog } from '@verclam/ui-kit';

const linkStyle = css`
	align-self: flex-start;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-family: ${theme.fonts.body};
	font-size: 0.8125rem;
	line-height: 1.5;
	text-decoration: underline;
	color: ${theme.colors.inkSecondary};
`;

const hintStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.75rem;
	line-height: 1.5;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
};

/**
 * Discreet underlined link that abandons a preparation not launched yet, behind a `ConfirmDialog` (nothing is lost:
 * interviews, notes and documents stay in the dossier). Leaf action component: it dispatches itself,
 * then goes back to the dossier.
 */
const AbandonPreparationAction: React.FC<Props> = ({ subject, preparationId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
	const [isAbandoning, setIsAbandoning] = React.useState(false);

	const handleOpenConfirm = React.useCallback(() => {
		setIsConfirmVisible(true);
	}, []);

	const handleConfirmHide = React.useCallback(() => {
		setIsConfirmVisible(false);
	}, []);

	const handleConfirm = React.useCallback(() => {
		setIsAbandoning(true);
		dispatch(abandonWorkflowRun(new AbandonWorkflowRun(facilityId, subject, preparationId)))
			.then((action) => {
				if (action.type === 'WORKFLOW_RUN_ABANDONED') {
					navigate(buildSubjectPath(subject));
				}
			})
			.catch(ignoreHandledError)
			.finally(() => {
				setIsAbandoning(false);
				setIsConfirmVisible(false);
			});
	}, [dispatch, navigate, facilityId, subject, preparationId]);

	return (
		<React.Fragment>
			<button type="button" css={linkStyle} onClick={handleOpenConfirm}>
				Abandonner cette préparation
			</button>
			<p css={hintStyle}>Aucun document n’a encore été généré.</p>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isConfirmVisible}
				onHide={handleConfirmHide}
				header="Abandonner cette préparation ?"
				message="Rien n'est perdu : les entretiens, les notes et les documents restent au dossier. Seul le regroupement disparaît."
				acceptLabel="Abandonner"
				rejectLabel="Annuler"
				acceptSeverity="danger"
				defaultFocus="reject"
				loading={isAbandoning}
				accept={handleConfirm}
			/>
		</React.Fragment>
	);
};

export default AbandonPreparationAction;
